import { useEffect, useState } from "react";
import DateTextInput, { isoToDdmmyyyy } from "../../../components/DateTextInput";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { assignCard, getLinkedCards } from "pages/GenericOrderDetailsPage/apis";
import DeskDialog, { Field, inputClass } from "pages/GenericOrderDetailsPage/components/DeskDialog";

export type CardLine = { id: string; label: string };

/**
 * Linking a prepaid card to a card line: one the customer already has on file,
 * or a new one typed in. The card number is typed once and sent; it is never
 * shown back, and the cards on file show only their last four digits. Core
 * decides what a line may carry, and its sentence is shown when it refuses.
 */
export default function AssignPrepaidCardDialog({
  open,
  onOpenChange,
  customerId,
  cardLines,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string | null;
  /** The card lines on the order that can take a card. */
  cardLines: CardLine[];
}) {
  const [lineId, setLineId] = useState(cardLines[0]?.id ?? "");
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [cardId, setCardId] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [issuer, setIssuer] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [expiryOk, setExpiryOk] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: cards, isLoading } = useQuery({
    queryKey: ["customerCards", customerId],
    queryFn: () => getLinkedCards(customerId as string),
    enabled: open && !!customerId,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (open) {
      setLineId(cardLines[0]?.id ?? "");
      setCardId("");
      setCardNumber("");
      setIssuer("");
      setExpiresOn("");
      setExpiryOk(true);
      setError(null);
    }
  }, [open]);
  // A customer with no card on file goes straight to entering one.
  useEffect(() => {
    if (open && cards && cards.length === 0) setMode("new");
    if (open && cards && cards.length > 0) setMode("existing");
  }, [open, cards]);

  const { mutate, isLoading: saving } = useMutation({
    mutationFn: assignCard,
    onSuccess: () => {
      toast.success("Card assigned");
      queryClient.invalidateQueries(["orderDetails"]);
      queryClient.invalidateQueries(["customerCards"]);
      onOpenChange(false);
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "The card could not be assigned."),
  });

  const digits = cardNumber.replace(/\s+/g, "");
  const ready = !!lineId && (mode === "existing" ? !!cardId : /^\d{16}$/.test(digits) && expiryOk);

  return (
    <DeskDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Assign prepaid card"
      description="Choose the card for this line."
      busy={saving}
      submitDisabled={!ready}
      error={error}
      onSubmit={() => {
        setError(null);
        mutate(
          mode === "existing"
            ? { lineId, cardId }
            : { lineId, cardNumber: digits, issuer, expiresOn },
        );
      }}
    >
      {cardLines.length > 1 ? (
        <Field label="Card line" htmlFor="card-line">
          <select id="card-line" className={inputClass} value={lineId} onChange={(e) => setLineId(e.target.value)}>
            {cardLines.map((line) => (
              <option key={line.id} value={line.id}>
                {line.label}
              </option>
            ))}
          </select>
        </Field>
      ) : null}

      <div role="tablist" aria-label="Which card" className="flex gap-2">
        {([
          ["existing", "A card on file"],
          ["new", "A new card"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={mode === key}
            onClick={() => setMode(key)}
            className={`cursor-pointer rounded-lg border px-4 py-2 text-sm ${
              mode === key
                ? "border-[#E1EBFC] bg-[#E1EBFC] text-[#165FE3]"
                : "border-[#F9F9F9] bg-[#F9F9F9] text-[#666] hover:bg-[#F0F4FF]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "existing" ? (
        isLoading ? (
          <p className="text-sm text-[#666666]">Fetching the customer's cards...</p>
        ) : cards?.length ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Cards on file</legend>
            {cards.map((card) => (
              <label
                key={card.id}
                className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm ${
                  cardId === card.id ? "border-blue-700 bg-blue-50" : "border-[#D2D2D2]"
                }`}
              >
                <input
                  type="radio"
                  name="card"
                  checked={cardId === card.id}
                  onChange={() => setCardId(card.id)}
                />
                <span className="flex flex-col">
                  <span className="font-medium text-[#333333]">{card.issuer || "Prepaid card"}</span>
                  <span className="text-[#666666]">
                    •••• {card.last4} · {card.status}
                    {` · ${card.expiresOn ? `expires ${isoToDdmmyyyy(card.expiresOn)}` : "no expiry date"}`}
                    {cards.filter((c) => c.last4 === card.last4 && c.issuer === card.issuer).length > 1
                      ? ` · card ${cards.filter((c) => c.last4 === card.last4 && c.issuer === card.issuer).findIndex((c) => c.id === card.id) + 1}`
                      : ""}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : (
          <p className="text-sm text-[#666666]">This customer has no card on file.</p>
        )
      ) : (
        <>
          <Field label="Card number" htmlFor="card-number" hint="16 digits. It is stored encrypted and never shown in full.">
            <input
              id="card-number"
              className={inputClass}
              inputMode="numeric"
              autoComplete="off"
              maxLength={19}
              placeholder="Enter 16 digits card number"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value.replace(/[^\d\s]/g, ""))}
            />
          </Field>
          <Field label="Card issuer" htmlFor="card-issuer">
            <input
              id="card-issuer"
              className={inputClass}
              placeholder="Who issued the card"
              value={issuer}
              onChange={(e) => setIssuer(e.target.value)}
            />
          </Field>
          <Field label="Expires on" htmlFor="card-expiry">
            <DateTextInput id="card-expiry" className={inputClass} value={expiresOn} onChange={setExpiresOn} onValidity={setExpiryOk} />
          </Field>
        </>
      )}
    </DeskDialog>
  );
}
